<template>
  <base-dialog
    :title="$t('employee.editEmployee')"
    :openDialog="openEditDialog"
    class="body-modal"
  >
    <el-form
      :model="editEmployeeForm"
      :rules="validator"
      ref="editEmployeeForm"
      label-position="top"
      @submit.native.prevent
    >
      <el-row :gutter="50" class="row-input">
        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('employee.name')" prop="name">
            <el-input
              class="input"
              v-model="editEmployeeForm.name"
              autocomplete="off"
            >
              <i
                slot="suffix"
                class="el-input__icon el-icon-circle-close el-input__clear"
                @click="editEmployeeForm.name = ''"
                v-if="editEmployeeForm.name"
              ></i>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('employee.email')" prop="email">
            <el-input
              class="input"
              v-model="editEmployeeForm.email"
              autocomplete="off"
            >
              <i
                slot="suffix"
                class="el-input__icon el-icon-circle-close el-input__clear"
                @click="editEmployeeForm.email = ''"
                v-if="editEmployeeForm.email"
              ></i>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="50" class="row-input">
        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('employee.phone')" prop="phone">
            <el-input
              class="input"
              v-model="editEmployeeForm.phone"
              autocomplete="off"
              @input="acceptNumber"
            >
              <i
                slot="suffix"
                class="el-input__icon el-icon-circle-close el-input__clear"
                @click="editEmployeeForm.phone = ''"
                v-if="editEmployeeForm.phone"
              ></i>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('employee.jobTitle')" prop="role">
            <el-input
              class="input"
              v-model="editEmployeeForm.role"
              autocomplete="off"
            >
              <i
                slot="suffix"
                class="el-input__icon el-icon-circle-close el-input__clear"
                @click="editEmployeeForm.role = ''"
                v-if="editEmployeeForm.role"
              ></i>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="50" class="row-input">
        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('employee.labelStore')" prop="storeId">
            <el-autocomplete
              class="input"
              ref="autocomplete"
              popper-class="popper-drop-up"
              :placeholder="$t('employee.storePlaceholder')"
              v-model="storeAutoComplete.label"
              :fetch-suggestions="remoteMethod"
              :popper-append-to-body="false"
              @select="handleSelect"
              @change="handleChange"
              @blur="handleBlur"
            >
              <i
                slot="suffix"
                class="el-input__icon el-icon-circle-close el-input__clear"
                @click="handleAutocomplete"
                v-if="storeAutoComplete.label"
              ></i>
            </el-autocomplete>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item>
        <span class="dialog-footer">
          <el-button
            class="btn btn--cancel"
            @click="handleCloseEditDialog('editEmployeeForm')"
          >
            {{ $t('employee.cancelBtn') }}
          </el-button>
          <el-button
            class="btn btn--submit"
            :disabled="editEmployeeLoading"
            @click="submitForm('editEmployeeForm')"
          >
            {{ $t('employee.editBtn') }}
          </el-button>
        </span>
      </el-form-item>
    </el-form>
  </base-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { convertHalfWidth, handleError, notifyMessage } from '@/helper';
import { email, name, phone, store } from '@/helper/validate';
import { employeeService } from '@/services/employee.service';
import { LIMIT_SELECT } from '@/constants/common';

const BaseDialog = () => import('@/components/Dialog');

export default {
  name: 'EditEmployeeDialog',
  components: { BaseDialog },
  props: {
    openEditDialog: Boolean,
  },
  data() {
    return {
      editEmployeeLoading: false,
      editEmployeeForm: {
        name: '',
        email: '',
        phone: '',
        role: '',
        storeId: null,
      },

      // Autocomplete
      storeAutoComplete: {
        label: '',
        options: [],
      },

      validator: {
        name: [{ validator: name, trigger: 'blur' }],
        phone: [{ validator: phone, trigger: 'blur' }],
        email: [{ validator: email, trigger: 'blur' }],
        storeId: [{ validator: store, trigger: 'blur' }],
      },
    };
  },
  computed: {
    ...mapGetters('store', ['store', 'error']),
    ...mapGetters('employee', ['employee']),
    mapStoreData() {
      return this.store.data.map((item) => {
        return { storeId: item.id, value: item.name };
      });
    },
  },
  methods: {
    ...mapActions('employee', ['getEmployeeDetail']),
    ...mapActions('store', ['getStoreList']),
    submitForm(editEmployeeForm) {
      const { id } = this.$router.currentRoute.params;

      this.$refs[editEmployeeForm].validate((valid) => {
        if (valid) {
          this.editEmployeeLoading = true;
          return employeeService
            .edit(
              {
                name: this.editEmployeeForm.name.trim(),
                email: this.editEmployeeForm.email
                  ? this.editEmployeeForm.email.trim()
                  : null,
                phone: this.editEmployeeForm.phone.trim(),
                role: this.editEmployeeForm.role.trim(),
                storeId: this.editEmployeeForm.storeId,
              },
              id
            )
            .then(() => {
              this.getEmployeeDetail({ id });
              this.$emit('handleCloseEditDialog');
              this.storeAutoComplete.label = null;
              notifyMessage(this.$t('common.editSuccess'), 'success');
            })
            .catch((error) => {
              handleError(error);
            })
            .finally(() => {
              this.editEmployeeLoading = false;
            });
        }
      });
    },
    handleCloseEditDialog(editEmployeeForm) {
      this.storeAutoComplete.label = null;
      this.$refs[editEmployeeForm].resetFields();
      this.storeAutoComplete.label = this.employee.store.name;
      this.$emit('handleCloseEditDialog');
    },

    /* Autocomplete */
    async remoteMethod(query, cb) {
      await this.getStoreList({
        limit: LIMIT_SELECT,
        freeText: query,
        onlyName: 1,
      });
      this.storeAutoComplete.options = this.mapStoreData || [];

      cb(this.storeAutoComplete.options);
    },
    handleAutocomplete() {
      this.storeAutoComplete.label = '';
      this.editEmployeeForm.storeId = null;
    },

    handleSelect(item) {
      this.editEmployeeForm.storeId = item.storeId;
    },
    handleChange() {
      this.editEmployeeForm.storeId = null;
    },
    handleBlur() {
      if (!this.editEmployeeForm.storeId) {
        this.storeAutoComplete.label = null;
      }
    },
    acceptNumber(value) {
      this.editEmployeeForm.phone = convertHalfWidth(value);
    },
  },
  mounted() {
    this.editEmployeeForm = {
      ...this.employee,
      store: null,
      storeId: this.employee.store.id,
    };

    this.storeAutoComplete.label = this.employee.store.name;
  },
  watch: {
    'editEmployeeForm.email'() {
      if (this.$refs['editEmployeeForm']) {
        this.$refs['editEmployeeForm'].clearValidate('email');
      }
    },
    'editEmployeeForm.name'() {
      if (this.$refs['editEmployeeForm']) {
        this.$refs['editEmployeeForm'].clearValidate('name');
      }
    },
    'editEmployeeForm.phone'() {
      if (this.$refs['editEmployeeForm']) {
        this.$refs['editEmployeeForm'].clearValidate('phone');
      }
    },
    'editEmployeeForm.role'() {
      if (this.$refs['editEmployeeForm']) {
        this.$refs['editEmployeeForm'].clearValidate('role');
      }
    },
    'editEmployeeForm.storeId'() {
      if (this.$refs['editEmployeeForm']) {
        this.$refs['editEmployeeForm'].clearValidate('storeId');
      }
    },
  },
};
</script>

<style lang="scss" scope>
.body-modal {
  .el-autocomplete {
    width: 100%;
  }

  .el-autocomplete-suggestion {
    margin: 0 !important;
  }
  .input {
    width: 100% !important;
  }
}
</style>
